import {
  COMPONENT_VALUE_TYPES as types,
  DIRECTIONS,
} from 'shared/constants';
import type { PropsSchemaList } from 'shared/types/PropsSchema';

export interface Props {
  editor?: object;
  background?: {
    useBackgroundColor?: boolean;
    backgroundColor?: string;
    desktopImage?: string;
    tabletImage?: string;
    mobileImage?: string;
  };
  header?: {
    title?: {
      text?: string;
      color?: string;
    };
    description?: {
      text?: string;
      color?: string;
    };
  };
  blocks?: {
    title?: string;
    description?: string;
  }[];
  blockTitleColor?: string;
  blockDescriptionColor?: string;
  dividerColor?: string;
  animation?: {
    enabled?: boolean;
    type?: 'FADE' | 'SLIDE' | 'SCALE';
    direction?: 'UP' | 'DOWN' | 'LEFT' | 'RIGHT';
    duration?: number | string;
    initialDelay?: number | string;
    stagger?: number | string;
    animateHeader?: boolean;
    animateBlocks?: boolean;
  };
}

const propsSchema: PropsSchemaList = [
  {
    key: 'background',
    label: 'Background',
    type: types.OBJECT,
    children: [
      {
        key: 'useBackgroundColor',
        label: 'Use Background Color',
        type: types.BOOLEAN,
      },
      {
        key: 'backgroundColor',
        label: 'Background Color',
        type: types.COLOR_PICKER,
      },
      {
        key: 'desktopImage',
        label: 'Desktop Background Image',
        type: types.S3_ASSET_URL,
      },
      {
        key: 'tabletImage',
        label: 'Tablet Background Image',
        type: types.S3_ASSET_URL,
      },
      {
        key: 'mobileImage',
        label: 'Mobile Background Image',
        type: types.S3_ASSET_URL,
      },
    ],
  },
  {
    key: 'header',
    label: 'Header',
    type: types.OBJECT,
    children: [
      {
        key: 'title',
        label: 'Title',
        type: types.OBJECT,
        children: [
          {
            key: 'text',
            label: 'Title Text',
            type: types.TEXT,
          },
          {
            key: 'color',
            label: 'Title Color',
            type: types.COLOR_PICKER,
          },
        ],
      },
      {
        key: 'description',
        label: 'Description',
        type: types.OBJECT,
        children: [
          {
            key: 'text',
            label: 'Description Text',
            type: types.TEXTAREA,
            options: {
              rows: 6,
            } as any,
          },
          {
            key: 'color',
            label: 'Description Color',
            type: types.COLOR_PICKER,
          },
        ],
      },
    ],
  },
  {
    key: 'blocks',
    label: 'Blocks',
    type: types.ARRAY,
    children: [
      {
        key: 'title',
        label: 'Block Title',
        type: types.TEXT,
      },
      {
        key: 'description',
        label: 'Block Description',
        type: types.TEXTAREA,
        options: {
          rows: 4,
        } as any,
      },
    ],
  },
  {
    key: 'blockTitleColor',
    label: 'Block Title Color',
    type: types.COLOR_PICKER,
  },
  {
    key: 'blockDescriptionColor',
    label: 'Block Description Color',
    type: types.COLOR_PICKER,
  },
  {
    key: 'dividerColor',
    label: 'Block Divider Color',
    type: types.COLOR_PICKER,
  },
  {
    key: 'animation',
    label: 'Animation',
    type: types.OBJECT,
    children: [
      {
        key: 'enabled',
        label: 'Enable Animation',
        type: types.BOOLEAN,
      },
      {
        key: 'type',
        label: 'Animation Type',
        type: types.ENUM,
        options: ['FADE', 'SLIDE', 'SCALE'],
      },
      {
        key: 'direction',
        label: 'Direction (for Slide)',
        type: types.ENUM,
        options: DIRECTIONS,
      },
      {
        key: 'duration',
        label: 'Duration (s)',
        type: types.TEXT,
      },
      {
        key: 'initialDelay',
        label: 'Initial Delay (s)',
        type: types.TEXT,
      },
      {
        key: 'stagger',
        label: 'Stagger (s)',
        type: types.TEXT,
        tooltip: 'Delay between each item\'s animation start.',
      },
      {
        key: 'animateHeader',
        label: 'Animate Header',
        type: types.BOOLEAN,
      },
      {
        key: 'animateBlocks',
        label: 'Animate Blocks',
        type: types.BOOLEAN,
      },
    ],
  },
];

const defaultProps: Props = {
  background: {
    useBackgroundColor: false,
    backgroundColor: '#000000',
    desktopImage: 'https://genesis-cdn.ff.com/cxc10/original/gold-ripple-top-right.jpg',
    tabletImage: '',
    mobileImage: '',
  },
  header: {
    title: {
      text: 'About the C10 Index',
      color: '#C6AA84',
    },
    description: {
      text: 'The first investable, exchange-traded basket index of the top ten cryptocurrencies announced by a U.S.-listed company — the C10 Index.\n\nCentered on Bitcoin and Ethereum, the index covers the top ten cryptocurrencies by free-float market capitalization, excluding stablecoins. It is calculated using market-cap weighted rules, with a maximum 50% allocation to any single asset.',
      color: 'rgba(201, 201, 209, 1)',
    },
  },
  blocks: [
    {
      title: 'Index Definition',
      description: 'The C10 Index tracks the performance of the top 10 cryptocurrencies by market capitalization, using a market-cap weighted methodology to provide diversified digital asset exposure.',
    },
    {
      title: 'Weight Rules',
      description: 'Market-cap based weight allocation with a maximum single asset weight of 50%, ensuring index diversification and risk distribution.',
    },
    {
      title: 'Mature Methodology',
      description: 'Employs proven index construction methods with transparent rules and rigorous governance processes, ensuring index reliability and consistency.',
    },
    {
      title: 'Regular Rebalancing',
      description: 'Monthly weight adjustments ensure the index always reflects the latest market conditions, maintaining optimal risk-return characteristics.',
    },
  ],
  blockTitleColor: 'rgba(253, 225, 186, 1)',
  blockDescriptionColor: 'rgba(171, 171, 171, 1)',
  dividerColor: 'rgba(255, 255, 255, 0.3)',
  animation: {
    enabled: false,
    type: 'SLIDE',
    direction: 'UP',
    duration: 0.8,
    initialDelay: 0,
    stagger: 0.2,
    animateHeader: true,
    animateBlocks: true,
  },
};

export default {
  title: 'C10 Description Blocks',
  id: 'C10DescriptionBlocks',
  propsSchema,
  defaultProps,
};
